import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';

import { Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';

import { TranslocoService } from '@jsverse/transloco';
import { ReplaySubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { TabObject } from '@inmoment/the-kitchen';

import { SimpleDialogButtonColor } from 'app/components/simple-dialog.component';
import { HasTitle } from 'app/has-title';
import { AuthService } from 'app/providers/bridge-api/auth.service';
import {
  ConfigurationRoute,
  ConfigurationRouteService,
} from 'app/providers/semantria-api/configuration-route.service';
import {
  ConfigurationService,
  SemantriaConfiguration,
} from 'app/providers/semantria-api/configuration.service';
import { StorageConfigurationService } from 'app/providers/storage-api/blender-configuration.service';
import { CollectionService } from 'app/providers/storage-api/collection.service';
import { AlertService } from 'app/services/alert.service';
import { DialogService } from 'app/services/dialog.service';
import {
  EnableClassTelemetry,
  EnableComponentTelemetry,
  injectComponentTelemetryService,
} from 'app/services/otel.service';

import { Utils } from 'app/utils/utils';

import { ConfigurationRouteEditComponent } from '../../components/configuration-route-edit/configuration-route-edit.component';
import { ConfigurationRouteListService } from '../../services/configuration-route-list.service';

@EnableClassTelemetry()
@Component({
  selector: 'app-configuration-route-view',
  templateUrl: './configuration-route-view.component.html',
  styleUrls: ['./configuration-route-view.component.scss'],
  standalone: false,
})
export class ConfigurationRouteViewComponent extends HasTitle implements OnInit, OnDestroy {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  @ViewChild('configRouteEditTab') configRouteEditTab: ConfigurationRouteEditComponent;

  configRouteTabs: TabObject[] = [];

  session;
  canEditRoute;

  selectedRoute: ConfigurationRoute;

  selectedConfiguration: SemantriaConfiguration;
  templateConfiguration: SemantriaConfiguration;

  autoAnalysisConfigs: string[] = [];
  hasAutoAnalysis;
  autoAnalysisDeleteTooltip;
  hasOngoingAnalysis;
  ongoingAnalysisDeleteTooltip;
  deleteButtonTooltip;

  activeTab = 'configurations';
  isTabChangeInProgress;
  isLoading = true;
  isSaving: boolean;

  groupId: string;

  routeId$ = new ReplaySubject<string>();
  unsub$ = new Subject<void>();

  constructor(
    protected titleService: Title,
    private configRouteService: ConfigurationRouteService,
    private configService: ConfigurationService,
    private authService: AuthService,
    private blenderConfigService: StorageConfigurationService,
    private collectionService: CollectionService,
    private dialogService: DialogService,
    private alertService: AlertService,
    private listService: ConfigurationRouteListService,
    private route: ActivatedRoute,
    private translocoService: TranslocoService
  ) {
    super(titleService);

    this.groupId = this.route.snapshot.paramMap.get('groupId');
    this.route.paramMap.pipe(takeUntil(this.unsub$)).subscribe((params) => {
      this.routeId$.next(params.get('routeId'));
    });
    this.listService.onRouteUpdated
      .pipe(takeUntil(this.unsub$))
      .subscribe((res) => (this.selectedRoute = res));
  }

  async ngOnInit() {
    this.session = await this.authService.getSession();
    this.autoAnalysisConfigs = (await this.collectionService.getAutoAnalysisConfigs()).items;

    this.canEditRoute = this.authService.canEditConfigurationRoute();

    this.translocoService
      .selectTranslateObject('configuration-routes.view')
      .pipe(takeUntil(this.unsub$))
      .subscribe((translations) => {
        this.configRouteTabs = [
          { title: translations['configurations-tab'].title, id: 'configurations' },
          { title: translations['settings-tab'].title, id: 'settings' },
        ];
        this.autoAnalysisDeleteTooltip = translations['no-delete-if-auto-analysis'];
        this.ongoingAnalysisDeleteTooltip = translations['no-delete-if-ongoing-analysis'];
      });

    this.routeId$.pipe(takeUntil(this.unsub$)).subscribe(async (routeId) => {
      this.isLoading = true;

      this.selectedRoute = await this.configRouteService.getConfigurationRoute(routeId);
      this.hasAutoAnalysis = this.autoAnalysisConfigs.includes('cr:' + this.selectedRoute.route_id);
      this.blenderConfigService
        .isRouteOrConfigurationInUse(routeId)
        .then((res) => {
          this.hasOngoingAnalysis = res.beingUsed;

          this.deleteButtonTooltip = undefined;
          if (this.hasAutoAnalysis) {
            this.deleteButtonTooltip = this.autoAnalysisDeleteTooltip;
          }
          if (this.hasOngoingAnalysis) {
            this.deleteButtonTooltip = this.ongoingAnalysisDeleteTooltip;
          }
        })
        .finally(() => (this.isLoading = false));
    });
  }

  ngOnDestroy(): void {
    this.unsub$.next();
  }

  async interceptTabChange(id: string) {
    if (this.isTabChangeInProgress || this.activeTab === id) {
      return;
    }
    this.isTabChangeInProgress = true;
    this.authService.extendSession();
    const promise = new Promise((resolve) => this.configRouteEditTab.checkChanges(resolve));
    const res = await promise;
    if (res) {
      this.activeTab = id;
    }
    this.isTabChangeInProgress = false;
    return;
  }

  public async onDeleteRoute(): Promise<void> {
    const configName = this.selectedRoute.name;
    const fullTitle = this.translocoService.translate('configuration-routes.view.delete.title', {
      route: configName,
    });
    const truncatedTitle = this.translocoService.translate(
      'configuration-routes.view.delete.title',
      { route: Utils.truncate(configName, 30) }
    );

    this.dialogService
      .showWarningPopup({
        title: truncatedTitle,
        titleTooltip: configName.length > 30 ? fullTitle : '',
        content: this.translocoService.translate('configuration-routes.view.delete.content'),
        confirmTitle: this.translocoService.translate(
          'configuration-routes.view.delete.confirm-button'
        ),
        confirmColor: SimpleDialogButtonColor.red,
      })
      .afterClosed()
      .subscribe(async (result) => {
        if (result) {
          this.isLoading = true;

          this.configRouteService
            .deleteConfigurationRoute(this.selectedRoute.route_id)
            .then(() => {
              this.alertService.showToast(
                this.translocoService.translate('configuration-routes.view.delete.delete-success', {
                  route: configName,
                })
              );
              this.listService.routeDeleted(this.selectedRoute);
              this.configService.clearCache();
            })
            .catch((err) => {
              this.alertService.showErrorToast(
                this.translocoService.translate('configuration-routes.view.delete.delete-failed', {
                  route: configName,
                }),
                err
              );
            })
            .finally(() => {
              this.isLoading = false;
            });
        }
      });
  }
}
